import * as yup from 'yup';
import type { OpportunityReportingOwnerScope } from './buildOpportunityReportingApiFilter';

/** Serializable subset stored in `?filters=` for shareable links. */
export interface OpportunityReportingUrlFilters {
	includeArchived?: boolean;
	stageId?: string;
	ownerScope?: OpportunityReportingOwnerScope;
	ownerUserId?: string;
	creationDateFrom?: string;
	creationDateTo?: string;
	closeDateFrom?: string;
	closeDateTo?: string;
}

export interface OpportunityReportingNormalizedFilters {
	includeArchived: boolean;
	filterStageId: string;
	ownerScope: OpportunityReportingOwnerScope;
	specificOwnerUserId: string;
	creationDateFrom: string;
	creationDateTo: string;
	closeDateFrom: string;
	closeDateTo: string;
}

const filterSchema = yup.object({
	includeArchived: yup.boolean().optional(),
	stageId: yup.string().optional(),
	ownerScope: yup.string().oneOf(['any', 'unassigned', 'specific']).optional(),
	ownerUserId: yup.string().optional(),
	creationDateFrom: yup.string().optional(),
	creationDateTo: yup.string().optional(),
	closeDateFrom: yup.string().optional(),
	closeDateTo: yup.string().optional(),
});

export function validateOpportunityReportingUrlFilters(value: unknown): OpportunityReportingUrlFilters | null {
	if (value === null || typeof value !== 'object' || Array.isArray(value)) {
		return null;
	}
	const raw = value as Record<string, unknown>;
	const picked = {
		includeArchived: raw.includeArchived,
		stageId: raw.stageId,
		ownerScope: raw.ownerScope,
		ownerUserId: raw.ownerUserId,
		creationDateFrom: raw.creationDateFrom,
		creationDateTo: raw.creationDateTo,
		closeDateFrom: raw.closeDateFrom,
		closeDateTo: raw.closeDateTo,
	};
	try {
		return filterSchema.validateSync(picked, { abortEarly: false }) as OpportunityReportingUrlFilters;
	} catch {
		return null;
	}
}

export function normalizeOpportunityReportingFilters(
	raw: OpportunityReportingUrlFilters | null | undefined
): OpportunityReportingNormalizedFilters {
	const scope = raw?.ownerScope;
	const ownerScope: OpportunityReportingOwnerScope =
		scope === 'unassigned' || scope === 'specific' || scope === 'any' ? scope : 'any';
	return {
		includeArchived: Boolean(raw?.includeArchived),
		filterStageId: raw?.stageId ?? '',
		ownerScope,
		specificOwnerUserId: ownerScope === 'specific' ? (raw?.ownerUserId ?? '') : '',
		creationDateFrom: raw?.creationDateFrom ?? '',
		creationDateTo: raw?.creationDateTo ?? '',
		closeDateFrom: raw?.closeDateFrom ?? '',
		closeDateTo: raw?.closeDateTo ?? '',
	};
}

/** Drops default-equivalent keys so share URLs stay short; `null` removes the query param. */
export function compactOpportunityReportingFiltersForUrl(
	f: OpportunityReportingNormalizedFilters
): OpportunityReportingUrlFilters | null {
	const out: OpportunityReportingUrlFilters = {};
	if (f.includeArchived) {
		out.includeArchived = true;
	}
	if (f.filterStageId) {
		out.stageId = f.filterStageId;
	}
	if (f.ownerScope !== 'any') {
		out.ownerScope = f.ownerScope;
	}
	if (f.ownerScope === 'specific' && f.specificOwnerUserId) {
		out.ownerUserId = f.specificOwnerUserId;
	}
	if (f.creationDateFrom) {
		out.creationDateFrom = f.creationDateFrom;
	}
	if (f.creationDateTo) {
		out.creationDateTo = f.creationDateTo;
	}
	if (f.closeDateFrom) {
		out.closeDateFrom = f.closeDateFrom;
	}
	if (f.closeDateTo) {
		out.closeDateTo = f.closeDateTo;
	}
	return Object.keys(out).length > 0 ? out : null;
}
